import React from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { useParams } from 'react-router-dom'
import { Container, Row } from 'reactstrap'
import {
  BootstrapTextInput,
  SaveButton,
  TypeaheadSingleInput,
  TypeaheadTagsInput,
  FormCard,
} from '../../components/forms'
import { _BSFT_SMS_CONFIG } from '../../utils/empties'
import { useItemMutation } from '../../utils/hooks/reactQuery/mutations'
import {
  useAllQuery,
  useSingleQuery,
} from '../../utils/hooks/reactQuery/queries'

const BsftSmsConfigSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  tags: Yup.array().of(Yup.string()),
  segmentUuid: Yup.string().required('Segment is required'),
  adapterUuid: Yup.string().required('Adapter is required'),
  fromName: Yup.string().required('From Name is required'),
})

function BsftSmsConfig() {
  const { id } = useParams()
  const {
    data = _BSFT_SMS_CONFIG,
    isLoading,
    isError,
    error,
  } = useSingleQuery('bsft-sms-config', id)
  const { update, add } = useItemMutation('bsft-sms-config')
  const { data: adapters = [] } = useAllQuery('bsft-sms-adapters')
  const { data: segments = [] } = useAllQuery('bsft-segments')
  const { data: rebrandlyDomains = [] } = useAllQuery('rebrandly-domains')

  const handleSubmit = (values, { setSubmitting }) => {
    const toastId = toast.loading('Saving sms config...')

    // Are we updating or adding?
    if (id !== 'new') {
      update.mutate(values, {
        onSuccess: () => {
          toast.update(toastId, {
            render: 'Saved sms config!',
            type: 'success',
            isLoading: false,
            autoClose: 1500,
          })
          setSubmitting(false)
        },
        onError: (error) => {
          console.error(error)
          toast.update(toastId, {
            render: 'Could not save sms config...',
            type: 'error',
            isLoading: false,
            autoClose: 1500,
          })
          setSubmitting(false)
        },
      })
    } else {
      add.mutate(values, {
        onSuccess: () => {
          toast.update(toastId, {
            render: 'Saved sms config!',
            type: 'success',
            isLoading: false,
            autoClose: 1500,
          })
        },
        onError: (error) => {
          console.error(error)
          toast.update(toastId, {
            render: 'Could not save sms config...',
            type: 'error',
            isLoading: false,
            autoClose: 1500,
          })
          setSubmitting(false)
        },
      })
    }
  }

  return (
    <Container fluid>
      {isLoading ? (
        <h5>Loading...</h5>
      ) : isError ? (
        <h5>Error: {error.message}</h5>
      ) : (
        <FormCard
          breadcrumbLink="/dashboard/bsft-sms-configs"
          breadcrumbLabel="SMS Configs"
          breadcrumbItem={data.name.length > 0 ? data.name : 'New SMS Config'}
        >
          <Formik
            initialValues={data}
            validationSchema={BsftSmsConfigSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ setFieldValue, isValid, isSubmitting }) => (
              <Form>
                <Row>
                  <BootstrapTextInput
                    label="Name"
                    id="name"
                    name="name"
                    placeholder="Name"
                  />
                  <TypeaheadTagsInput />
                  <TypeaheadSingleInput
                    label="Segment"
                    id="segmentUuid"
                    name="segmentUuid"
                    placeholder="Segment"
                    options={segments}
                    labelKey="name"
                    valueKey="uuid"
                    objects
                    renderMenuItemChildren={(option) => (
                      <div>
                        {option.name}
                        <div>
                          <small>SMS Users: {option.sms_users}</small>
                        </div>
                      </div>
                    )}
                    customOnChange={(selected) => {
                      if (selected !== '') {
                        setFieldValue('userCount', selected.sms_users)
                        setFieldValue(
                          'userCountLastUpdated',
                          selected.approxusers_updated_at,
                        )
                        setFieldValue('segmentUuid', selected.uuid, true)
                      } else {
                        setFieldValue('userCount', 0)
                        setFieldValue('userCountLastUpdated', '')
                        setFieldValue('segmentUuid', '', true)
                      }
                    }}
                  />
                  <TypeaheadSingleInput
                    label="Adapter"
                    id="adapterUuid"
                    name="adapterUuid"
                    placeholder="Adapter"
                    options={adapters}
                    labelKey="primary_name"
                    valueKey="uuid"
                    objects
                    renderMenuItemChildren={(option) => (
                      <div>
                        {option.primary_name}
                        <div>
                          <small>{option.name}</small>
                        </div>
                      </div>
                    )}
                  />
                  <BootstrapTextInput
                    label="From Name"
                    id="fromName"
                    name="fromName"
                    placeholder="From Name"
                  />
                  <TypeaheadSingleInput
                    label="Rebrandly Domain"
                    id="rebrandlyDomain"
                    name="rebrandlyDomain"
                    placeholder="Rebrandly Domain"
                    options={rebrandlyDomains}
                    labelKey="fullName"
                    valueKey="id"
                    objects
                  />
                </Row>
                <SaveButton disabled={isSubmitting || !isValid} />
              </Form>
            )}
          </Formik>
        </FormCard>
      )}
    </Container>
  )
}

export default BsftSmsConfig
